import PageBanner from '../../components/shared/PageBanner';
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Popconfirm, Select, Spin, Tabs, Upload, message } from 'antd';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    DocumentProjectFileModel,
    ProjectListModel,
    createProject,
    deleteProject,
    deleteProjectFile,
    getAllProjects,
    getProjectFiles,
    uploadProjectFile,
} from '../../api/documentProjectApi';
import CompanyProjectAccess from './CompanyProjectAccess';

const { Option } = Select;
const { TabPane } = Tabs;

interface UploadFormState {
    title: string;
    kind: 'pdf' | 'spreadsheet';
    file: File | null;
}

const DocumentSettings: FunctionComponent = () => {
    const history = useHistory();
    const preventContextMenu = (e: React.MouseEvent) => e.preventDefault();

    const [projects, setProjects] = useState<ProjectListModel[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
    const [files, setFiles] = useState<DocumentProjectFileModel[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState<UploadFormState>({ title: '', kind: 'pdf', file: null });
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [creatingProject, setCreatingProject] = useState(false);

    useEffect(() => {
        getAllProjects()
            .then((list) => {
                setProjects(list);
                if (list.length > 0) setSelectedProjectId(list[0].id);
            })
            .catch(() => message.error('Failed to load projects'))
            .finally(() => setLoadingProjects(false));
    }, []);

    const loadFiles = useCallback((projectId: number) => {
        setLoadingFiles(true);
        getProjectFiles(projectId)
            .then(setFiles)
            .catch(() => message.error('Failed to load files'))
            .finally(() => setLoadingFiles(false));
    }, []);

    useEffect(() => {
        if (selectedProjectId != null) loadFiles(selectedProjectId);
    }, [selectedProjectId, loadFiles]);

    const handleUpload = async () => {
        if (!selectedProjectId) { message.warning('Select a project first.'); return; }
        if (!form.title.trim()) { message.warning('Enter a document title.'); return; }
        if (!form.file) { message.warning('Choose a file to upload.'); return; }
        setUploading(true);
        try {
            const created = await uploadProjectFile(selectedProjectId, form.title.trim(), form.kind, form.file);
            setFiles((prev) => [...prev, created]);
            setForm({ title: '', kind: 'pdf', file: null });
            message.success('File uploaded successfully.');
        } catch {
            message.error('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleCreateProject = async () => {
        if (!newProjectName.trim()) { message.warning('Enter a project name.'); return; }
        setCreatingProject(true);
        try {
            const created = await createProject(newProjectName.trim());
            setProjects((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
            setSelectedProjectId(created.id);
            setNewProjectName('');
            setCreateModalOpen(false);
            message.success(`Project "${created.name}" created.`);
        } catch {
            message.error('Failed to create project.');
        } finally {
            setCreatingProject(false);
        }
    };

    const handleDelete = async (fileId: number) => {
        try {
            await deleteProjectFile(fileId);
            setFiles((prev) => prev.filter((f) => f.id !== fileId));
            message.success('File deleted.');
        } catch {
            message.error('Failed to delete file.');
        }
    };

    const handleDeleteProject = async (projectId: number) => {
        try {
            await deleteProject(projectId);
            const next = projects.filter((p) => p.id !== projectId);
            setProjects(next);
            setSelectedProjectId(next.length > 0 ? next[0].id : null);
            setFiles([]);
            message.success('Project deleted.');
        } catch {
            message.error('Failed to delete project.');
        }
    };

    const filesTab = (
        <div style={{ display: 'grid', gap: 20, maxWidth: 860 }}>
            {/* Project picker */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 15, color: '#111827' }}>Select Project</h3>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button icon={<PlusOutlined />} size="small" onClick={() => setCreateModalOpen(true)}>
                            New Project
                        </Button>
                        {selectedProjectId != null && (
                            <Popconfirm
                                title="Delete this project?"
                                description="All files in this project will also be deleted. This cannot be undone."
                                onConfirm={() => handleDeleteProject(selectedProjectId)}
                                okText="Delete"
                                okButtonProps={{ danger: true }}
                                cancelText="Cancel"
                            >
                                <Button danger icon={<DeleteOutlined />} size="small">Delete Project</Button>
                            </Popconfirm>
                        )}
                    </div>
                </div>
                {loadingProjects ? <Spin size="small" /> : projects.length === 0 ? (
                    <div style={{ color: '#6b7280' }}>No projects yet. Click "New Project" to create one.</div>
                ) : (
                    <Select
                        style={{ width: '100%' }}
                        value={selectedProjectId ?? undefined}
                        onChange={(val) => setSelectedProjectId(val)}
                        placeholder="Select a project…"
                    >
                        {projects.map((p) => (
                            <Option key={p.id} value={p.id}>{p.name}</Option>
                        ))}
                    </Select>
                )}
            </div>

            <Modal
                title="Create New Project"
                visible={createModalOpen}
                onOk={handleCreateProject}
                onCancel={() => { setCreateModalOpen(false); setNewProjectName(''); }}
                confirmLoading={creatingProject}
                okText="Create"
            >
                <div style={{ paddingTop: 8 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#374151' }}>Project Name</label>
                    <Input
                        placeholder="e.g. Mahindra Tower — Phase 2"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        onPressEnter={handleCreateProject}
                        autoFocus
                    />
                </div>
            </Modal>

            {/* Upload panel */}
            {selectedProjectId != null && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#111827' }}>Upload New File</h3>
                    <div style={{ display: 'grid', gap: 12 }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#374151' }}>Document Title</label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                placeholder="Enter document title…"
                                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#374151' }}>File Type</label>
                            <Select style={{ width: 200 }} value={form.kind} onChange={(val) => setForm((f) => ({ ...f, kind: val, file: null }))}>
                                <Option value="pdf">PDF</Option>
                                <Option value="spreadsheet">Spreadsheet (Excel)</Option>
                            </Select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#374151' }}>File</label>
                            <Upload
                                beforeUpload={(file) => {
                                    const isPdf = form.kind === 'pdf';
                                    const isSpreadsheet = form.kind === 'spreadsheet';
                                    const ext = file.name.split('.').pop()?.toLowerCase();
                                    if (isPdf && ext !== 'pdf') {
                                        message.error('Only PDF files (.pdf) are allowed for this document type.');
                                        return Upload.LIST_IGNORE;
                                    }
                                    if (isSpreadsheet && ext !== 'xls' && ext !== 'xlsx') {
                                        message.error('Only Excel files (.xls, .xlsx) are allowed for this document type.');
                                        return Upload.LIST_IGNORE;
                                    }
                                    setForm((f) => ({ ...f, file }));
                                    return false;
                                }}
                                onRemove={() => setForm((f) => ({ ...f, file: null }))}
                                maxCount={1}
                                accept={form.kind === 'pdf' ? '.pdf' : '.xls,.xlsx'}
                                fileList={form.file ? [{ uid: '-1', name: form.file.name, status: 'done' }] : []}
                            >
                                <Button icon={<UploadOutlined />}>
                                    {form.kind === 'pdf' ? 'Select PDF' : 'Select Excel File'}
                                </Button>
                            </Upload>
                        </div>
                        <Button type="primary" loading={uploading} onClick={handleUpload} style={{ width: 'fit-content' }} disabled={!form.title.trim() || !form.file}>
                            Upload
                        </Button>
                    </div>
                </div>
            )}

            {/* Files list */}
            {selectedProjectId != null && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#111827' }}>Files in This Project</h3>
                    {loadingFiles ? <Spin size="small" /> : files.length === 0 ? (
                        <div style={{ color: '#9ca3af', fontSize: 13 }}>No files uploaded yet.</div>
                    ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                            {files.map((file) => (
                                <div key={file.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px' }}>
                                    <div>
                                        <div style={{ fontWeight: 500 }}>{file.title}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>{file.kind === 'pdf' ? 'PDF' : 'Spreadsheet'}</div>
                                    </div>
                                    <Popconfirm title="Delete this file?" onConfirm={() => handleDelete(file.id)} okText="Delete" cancelText="Cancel">
                                        <Button danger icon={<DeleteOutlined />} size="small">Delete</Button>
                                    </Popconfirm>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="documents-page documents-page--with-app-header documents-page--manage" onContextMenu={preventContextMenu}>
            <div className="documents-page__inner">
                <PageBanner title="Manage Deal Room" storageKey="banner-manage-documents">
                    <div className="documents-page__subtitle">Upload files and control which companies can view each project.</div>
                </PageBanner>

                <Button type="text" onClick={() => history.push('/documents')} style={{ paddingLeft: 0, marginBottom: 16 }}>
                    ← Back to Deal Room
                </Button>

                <Tabs defaultActiveKey="files" size="large">
                    <TabPane tab="Upload Files" key="files">
                        {filesTab}
                    </TabPane>
                    <TabPane tab="Company Access" key="access">
                        <CompanyProjectAccess />
                    </TabPane>
                </Tabs>
            </div>
        </div>
    );
};

export default DocumentSettings;
